import React from 'react'
import Head from 'next/head'
import styled from '@emotion/styled'
import { mq } from '../../helpers'
import logger from '../../services/logger'
import { getPressArticles } from '../../services/strapi'
import { selectFeatured } from '../../helpers/press'
import { toJsonLdString } from '../../helpers/faq'
import PressCard from '../../components/press/PressCard'
import { PressGrid } from '../../components/press/styles'
import organization from '../../schemas/organization.json'

export async function getServerSideProps({ res }) {
  logger.log('Press.getServerSideProps: fetching press articles from Strapi')
  const articles = await getPressArticles()
  if (articles.length > 0) {
    res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=86400')
  }
  return { props: { articles } }
}

export default function Press({ articles }) {
  const { featured, rest } = selectFeatured(articles)

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'In the Press | GLAMSQUAD',
    description: 'Top and trending articles about Glamsquad.',
    url: 'https://glamsquad.com/press',
    publisher: organization,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: (articles || []).map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://glamsquad.com/press/${a.slug}`,
        name: a.title,
      })),
    },
  }

  return (
    <>
      <Head>
        <title>In the Press | GLAMSQUAD</title>
        <meta name="description" content="GLAMSQUAD in the news. Read top and trending media coverage and press features about the leading on-demand, in-home beauty services platform." />
        <link rel="canonical" href="https://glamsquad.com/press" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="In the Press | GLAMSQUAD" />
        <meta property="og:description" content="Top and trending articles about Glamsquad." />
        <meta property="og:url" content="https://glamsquad.com/press" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="In the Press | GLAMSQUAD" />
        <meta name="twitter:description" content="Top and trending articles about Glamsquad." />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLdString(collectionSchema) }} />
      </Head>
      <PressWrapper>
        <div className="header-container">
          <h1>In the Press</h1>
          <p>Top and Trending articles about Glamsquad</p>
        </div>
        {featured ? (
          <PressGrid>
            <PressCard article={featured} featured />
            {rest.map((article) => (
              <PressCard key={article.slug} article={article} />
            ))}
          </PressGrid>
        ) : (
          <p className="empty">No press coverage yet — check back soon.</p>
        )}
      </PressWrapper>
    </>
  )
}

const PressWrapper = styled.div`
  max-width: 1660px;
  margin: 0 auto;

  .header-container {
    padding: 103px 88px 40px;
    ${mq('max', '768px')} {
      padding: 30px 16px 20px;
    }
    h1 {
      color: var(--Black, #000);
      font-family: Garnett;
      font-size: 82px;
      font-weight: 750;
      line-height: 138%;
      letter-spacing: 1.64px;
      text-transform: uppercase;
      margin: 0;
      ${mq('max', '768px')} {
        font-size: 28px;
      }
    }
    p {
      color: var(--Dark-grey, #545454);
      font-family: Euclid;
      font-size: 18px;
      margin: 8px 0 0;
    }
  }

  .empty {
    padding: 0 88px 60px;
    font-family: Euclid;
    color: var(--Dark-grey, #545454);
    ${mq('max', '768px')} {
      padding: 0 16px 40px;
    }
  }
`
